import React, { useCallback, useEffect, useState } from "react";
import UIkit from "uikit";
import { useHistory, useParams } from "react-router-dom";
import { PERMISSION_GRID_GROUPS, ALL_MODULE_KEYS, ACCESS_LEVELS } from "./permissions_catalog";
import { usePermissionsStore } from "./permissions_store";
import rolesService from "../../services/roles.service";

const buildEmptyPermissionMap = () => {
  const map = {};
  ALL_MODULE_KEYS.forEach((moduleKey) => {
    map[moduleKey] = "none";
  });
  return map;
};

const getEditableModuleKeys = (items) => [...new Set(
  items
    .map((item) => item.moduleKey)
    .filter(Boolean),
)];

const DEFAULT_LEVELS = ["none", "view", "edit"];

const getAllowedLevels = (item) => item.accessLevels || DEFAULT_LEVELS;

const groupSupportsLevel = (items, level) => items.some((item) => (
  item.moduleKey && getAllowedLevels(item).includes(level)
));

const clampLevel = (item, level) => {
  const allowedLevels = getAllowedLevels(item);
  if (allowedLevels.includes(level)) return level;
  if (level === "edit" && allowedLevels.includes("view")) return "view";
  return allowedLevels[0] || "none";
};

const getModuleItemsMap = () => {
  const map = {};
  PERMISSION_GRID_GROUPS.forEach((group) => {
    group.items.forEach((item) => {
      if (!item.moduleKey || map[item.moduleKey]) return;
      map[item.moduleKey] = item;
    });
  });
  return map;
};

const MODULE_ITEMS = getModuleItemsMap();
const MODULE_GRID_ITEMS = PERMISSION_GRID_GROUPS.flatMap((group) => group.items);
const MODULE_GRID_ITEMS_BY_KEY = MODULE_GRID_ITEMS.reduce((acc, item) => {
  if (!item.moduleKey) return acc;
  if (!acc[item.moduleKey]) acc[item.moduleKey] = [];
  acc[item.moduleKey].push(item);
  return acc;
}, {});
const GROUP_NAMES = PERMISSION_GRID_GROUPS.map((group) => group.group);

export const buildPermissionsPayload = (permMap) => [...new Set([...ALL_MODULE_KEYS, ...Object.keys(permMap)])].map((moduleKey) => {
  const rawLevel = permMap[moduleKey] || "none";
  return {
    module_key: moduleKey,
    access_level: rawLevel,
  };
});

const projectItemLevel = (item, actualLevel) => clampLevel(item, actualLevel || "none");

const getBulkLevelForItems = (items, level) => {
  if (level === "none") return "none";
  if (level === "edit") {
    if (items.some((item) => getAllowedLevels(item).includes("edit"))) return "edit";
    if (items.some((item) => getAllowedLevels(item).includes("view"))) return "view";
    return "none";
  }
  if (items.some((item) => getAllowedLevels(item).includes("view"))) return "view";
  return "none";
};

const accessLevelLegendStyle = (level) => {
  const colors = {
    none: { background: "#fff", color: "var(--dark-purple)", border: "var(--border-color)" },
    view: { background: "var(--hms-blue-bg)", color: "#fff", border: "var(--hms-blue-bg)" },
    edit: { background: "var(--green)", color: "#fff", border: "var(--green)" },
  };
  const color = colors[level] || colors.none;
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 44,
    height: 22,
    padding: "0 9px",
    marginRight: 8,
    borderRadius: 4,
    border: `1px solid ${color.border}`,
    background: color.background,
    color: color.color,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: "20px",
  };
};

export default function RoleEditorPage() {
  const { roleId } = useParams();
  const history = useHistory();
  const { isOwner, canEdit, loadEffectivePermissions, updateRole, setRolePermissions } = usePermissionsStore();

  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // permission map: { module_key: 'none' | 'view' | 'edit' }
  const [permMap, setPermMap] = useState({});
  const [meta, setMeta] = useState({ name: "", description: "" });
  const [openGroups, setOpenGroups] = useState(() => GROUP_NAMES.slice(0, 1));

  const canManage = canEdit("admin.roles") && (isOwner || !role?.permissions?.some((p) => p.module_key.startsWith("admin.") && p.access_level !== "none"));

  const applyRolePayload = useCallback((payload) => {
    setRole(payload);
    setMeta({ name: payload.name, description: payload.description || "" });
    const map = buildEmptyPermissionMap();
    (payload.permissions || []).forEach((permission) => {
      if (permission.module_key) {
        map[permission.module_key] = permission.access_level || "none";
      }
    });
    setPermMap(map);
  }, []);

  useEffect(() => {
    setLoading(true);
    rolesService.getRole(roleId)
      .then((resp) => {
        applyRolePayload(resp.data);
      })
      .catch(() => UIkit.notification("Failed to load role", { status: "danger" }))
      .finally(() => setLoading(false));
  }, [roleId, applyRolePayload]);

  const getLevel = (moduleKey) => permMap[moduleKey] || "none";

  const getItemLevel = (item) => projectItemLevel(item, getLevel(item.moduleKey));

  const setLevel = (item, level) => {
    if (role?.is_system_role || !canManage || (!isOwner && item.moduleKey.startsWith("admin."))) return; // guard
    const moduleItems = MODULE_GRID_ITEMS_BY_KEY[item.moduleKey] || [item];
    setPermMap((prev) => ({
      ...prev,
      [item.moduleKey]: getBulkLevelForItems(moduleItems, level),
    }));
  };

  const setGroupLevel = (groupItems, level) => {
    if (role?.is_system_role || !canManage) return;
    setPermMap((prev) => {
      const next = { ...prev };
      [...new Set(groupItems.map((item) => item.moduleKey).filter(Boolean))].forEach((moduleKey) => {
        if (!isOwner && moduleKey.startsWith("admin.")) return;
        const moduleItems = groupItems.filter((item) => item.moduleKey === moduleKey);
        next[moduleKey] = getBulkLevelForItems(moduleItems, level);
      });
      return next;
    });
  };

  const setAllLevel = (level) => {
    if (role?.is_system_role || !canManage) return;
    const next = { ...permMap };
    ALL_MODULE_KEYS.forEach((moduleKey) => {
      if (!isOwner && moduleKey.startsWith("admin.")) return;
      const moduleItems = MODULE_GRID_ITEMS_BY_KEY[moduleKey] || [MODULE_ITEMS[moduleKey]].filter(Boolean);
      next[moduleKey] = getBulkLevelForItems(moduleItems, level);
    });
    setPermMap(next);
  };

  const toggleGroup = (groupName) => {
    setOpenGroups((prev) => (
      prev.includes(groupName)
        ? prev.filter((name) => name !== groupName)
        : [...prev, groupName]
    ));
  };

  const expandAllGroups = () => {
    setOpenGroups(GROUP_NAMES);
  };

  const collapseAllGroups = () => {
    setOpenGroups([]);
  };

  const saveMeta = async () => {
    if (!meta.name.trim()) {
      UIkit.notification("Role name is required", { status: "warning" });
      return;
    }
    setSaving(true);
    try {
      const updatedRole = await updateRole(roleId, {
        name: meta.name.trim(),
        description: meta.description.trim(),
      });
      setRole((prev) => ({ ...prev, ...updatedRole }));
      UIkit.notification("Role details saved", { status: "success" });
    } catch {
      UIkit.notification("Failed to save role", { status: "danger" });
    } finally {
      setSaving(false);
    }
  };

  const savePermissions = async () => {
    setSaving(true);
    const permissions = buildPermissionsPayload(permMap);
    try {
      const updatedRole = await setRolePermissions(roleId, permissions);
      applyRolePayload(updatedRole);
      await loadEffectivePermissions();
      UIkit.notification("Permissions saved", { status: "success" });
    } catch (err) {
      const apiError = err?.response?.data?.error;
      const modules = err?.response?.data?.modules;
      const details = Array.isArray(modules) && modules.length > 0
        ? `: ${modules.join(", ")}`
        : "";
      UIkit.notification(apiError ? `${apiError}${details}` : "Failed to save permissions", { status: "danger" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section>
        <div id="hms-page-title" className="uk-text-center uk-padding">
          <span data-uk-spinner="ratio: 2;"></span>
        </div>
      </section>
    );
  }

  if (!role) {
    return (
      <section>
        <div id="hms-page-title"><h1 className="uk-heading-small">Role not found</h1></div>
      </section>
    );
  }

  const isSystem = role.is_system_role;

  return (
    <section id="role-editor-page">
      <div id="hms-page-title" className="uk-flex uk-flex-between uk-flex-middle">
        <div className="uk-flex uk-flex-middle">
          <button
            className="uk-button hms-img-btn go-back-arrow-btn uk-margin-small-right"
            onClick={() => history.push("/roles")}
          >
            <span uk-icon="icon: arrow-left; ratio: 1"></span>
          </button>
          <div>
          <h1 className="uk-heading-small" style={{ margin: 0 }}>
            {isSystem ? "View Role" : "Edit Role"}: {role.name}
          </h1>
          {isSystem && (
            <span className="uk-badge uk-margin-small-top" style={{ background: "#1e87f0" }}>
              System Role — Read Only
            </span>
          )}
          </div>
        </div>
      </div>

      <div id="hms-main-body">
        {/* Role metadata */}
        {!isSystem && canManage && (
          <div className="uk-card uk-card-default uk-card-small hms-form-card uk-margin-bottom">
            <div className="uk-grid-small" uk-grid="true">
              <div className="uk-width-1-2@s">
                <label className="uk-form-label">Role Name</label>
                <input
                  className="uk-input"
                  value={meta.name}
                  onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))}
                />
              </div>
              <div className="uk-width-1-2@s">
                <label className="uk-form-label">Description</label>
                <input
                  className="uk-input"
                  value={meta.description}
                  onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))}
                />
              </div>
            </div>
            <div className="uk-flex uk-flex-right uk-margin-top">
              <button className="uk-button hms-btn hms-red-btn" onClick={saveMeta} disabled={saving}>
                Save
              </button>
            </div>
          </div>
        )}

        {/* Permissions matrix */}
        <div className="uk-card uk-card-default uk-card-small hms-form-card">
          <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-bottom">
            <div>
              <div className="semi-bold-font">Permissions</div>
              <div className="secondary">
                {isSystem
                  ? "System role permissions are fixed."
                  : "Set the access level for each module."}
              </div>
            </div>
            {!isSystem && canManage && (
              <div className="uk-flex" style={{ gap: 8 }}>
                <button className="uk-button hms-btn hms-white-btn" onClick={expandAllGroups}>
                  Expand All
                </button>
                <button className="uk-button hms-btn hms-white-btn" onClick={collapseAllGroups}>
                  Collapse All
                </button>
                <button className="uk-button hms-btn hms-white-btn" onClick={() => setAllLevel("none")}>
                  Clear All
                </button>
                <button className="uk-button hms-btn hms-blue-btn" onClick={() => setAllLevel("view")}>
                  View All
                </button>
                <button className="uk-button hms-btn hms-green-btn" onClick={() => setAllLevel("edit")}>
                  Full Access
                </button>
              </div>
            )}
          </div>

          <div className="uk-flex uk-flex-wrap uk-margin-large-bottom" style={{ gap: 16 }}>
            {ACCESS_LEVELS.map((al) => (
              <div key={al.value} className="uk-flex uk-flex-middle">
                <span style={accessLevelLegendStyle(al.value)}>
                  {al.label}
                </span>
                <span className="secondary">{al.description}</span>
              </div>
            ))}
          </div>

          <ul className="uk-accordion uk-margin-large-top">
            {PERMISSION_GRID_GROUPS.map((group) => {
              const editableKeys = getEditableModuleKeys(group.items);
              const showNoneAction = groupSupportsLevel(group.items, "none");
              const showViewAction = groupSupportsLevel(group.items, "view");
              const showEditAction = groupSupportsLevel(group.items, "edit");
              const isOpen = openGroups.includes(group.group);
              return (
                <li key={group.group} className={isOpen ? "uk-open" : ""}>
                  <a className="uk-accordion-title" href="#" onClick={(e) => {
                    e.preventDefault();
                    toggleGroup(group.group);
                  }} aria-expanded={isOpen}>
                    {group.group}
                  </a>
                  <div className="uk-accordion-content" hidden={!isOpen}>
                    {!isSystem && canManage && editableKeys.length > 0 && (
                      <div className="uk-flex uk-flex-right uk-margin-small-bottom" style={{ gap: 4 }}>
                        {showNoneAction && (
                          <button
                            className="uk-button hms-btn hms-white-btn"
                            onClick={() => setGroupLevel(group.items, "none")}
                          >None</button>
                        )}
                        {showViewAction && (
                          <button
                            className="uk-button hms-btn hms-blue-btn"
                            onClick={() => setGroupLevel(group.items, "view")}
                          >View</button>
                        )}
                        {showEditAction && (
                          <button
                            className="uk-button hms-btn hms-green-btn"
                            onClick={() => setGroupLevel(group.items, "edit")}
                          >Full Access</button>
                        )}
                      </div>
                    )}

                    <div className="uk-overflow-auto">
                      <table className="uk-table uk-table-divider uk-table-small" style={{ minWidth: 600 }}>
                        <thead>
                          <tr>
                            <th style={{ width: "26%" }}>Module</th>
                            <th style={{ width: "38%" }}>Permission</th>
                            <th className="uk-text-center" style={{ width: "12%" }}>None</th>
                            <th className="uk-text-center" style={{ width: "12%" }}>View</th>
                            <th className="uk-text-center" style={{ width: "12%" }}>Full Access</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.items.map((item) => {
                            const current = item.moduleKey ? getItemLevel(item) : "none";
                            const allowedLevels = getAllowedLevels(item);
                            const unsupported = !item.moduleKey;
                            return (
                              <tr key={item.id}>
                                <td>{item.moduleLabel}</td>
                                <td className="secondary">{item.permissionLabel}</td>
                                {unsupported ? (
                                  <td colSpan={3} className="uk-text-center secondary">
                                    Not backed by a saved role permission yet
                                  </td>
                                ) : (
                                  DEFAULT_LEVELS.map((level) => (
                                    <td key={level} className="uk-text-center">
                                      {allowedLevels.includes(level) ? (
                                        <input
                                          type="radio"
                                          name={item.id}
                                          value={level}
                                          checked={current === level}
                                          onChange={() => setLevel(item, level)}
                                          disabled={isSystem || !canManage || (!isOwner && item.moduleKey.startsWith("admin."))}
                                          className="uk-radio role-permission-radio"
                                        />
                                      ) : (
                                        <span className="secondary">-</span>
                                      )}
                                    </td>
                                  ))
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {!isSystem && canManage && (
            <div className="uk-flex uk-flex-right uk-margin-top">
              <button
                className="uk-button hms-btn hms-red-btn"
                onClick={savePermissions}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save Permissions"}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
